/**
 * FeedbackPage - コーチ確認画面
 *
 * 上段: 指摘 ⇔ 文字起こしハイライト連動ビュー（ReviewPage と同じ ReviewTranscriptPanel を使用）
 *   - 主発言は常に赤、関連発言（supporting）は選択中のカードに紐づくもののみ amber
 *   - 選択中の発話は省略解除で全文表示
 *   - 指摘番号と文字起こし左の番号バッジが連動
 *
 * 指摘一覧:
 *   - AI指摘ごとのフィードバッカー判定表示
 *   - 「確認完了」ボタン（coach + coach_ack_pending のみ）
 *
 * バックエンド API:
 *   - GET /sessions/{id}                : セッション概要
 *   - GET /sessions/{id}/review         : ReviewSessionDetailResponse（transcript + session_feedback_items）
 *   - GET /sessions/{id}/media-url      : Presigned S3 GET URL（自動ロード）
 *   - POST /sessions/{id}/coach_ack     : 確認完了
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Navigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuthStore } from '@/state/authStore';
import { useReviewSession } from '@/features/review/useReviewSession';
import { ReviewTranscriptPanel } from '@/features/review/ReviewTranscriptPanel';
import { OverallAnalysisPanel } from '@/features/review/OverallAnalysisPanel';
import { MediaPlaybackPanel } from '@/components/review/MediaPlaybackPanel';
import { SessionMeetingSummary } from '@/components/review/SessionMeetingSummary';
import { useMediaPlayer, type MediaKind } from '@/hooks/useMediaPlayer';
import {
  reviewTranscriptBlocksToTimeline,
} from '@/types/review';
import { LoadingState } from '@/components/Spinner';
import { formatSessionDay } from '@/utils/meetingType';
import { SESSION_PURGED_MESSAGE } from '@/lib/purge';

interface SessionMediaUrlResponse {
  url: string;
  kind: MediaKind;
  content_type?: string | null;
  filename?: string | null;
  role?: string | null;
  source?: string | null;
  expires_in: number;
}

export function FeedbackPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.currentUser);
  const [session, setSession] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(false);
  const [ackError, setAckError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'feedback' | 'analysis'>(() =>
    searchParams.get('tab') === 'analysis' ? 'analysis' : 'feedback',
  );

  // 文字起こし + 指摘連動ビュー用 detail
  const {
    detail: reviewDetail,
    loading: reviewDetailLoading,
    error: reviewDetailError,
  } = useReviewSession(sessionId);

  // メディア再生（アップロード済み S3 メディアを Presigned URL 経由で自動ロード）
  const audioTimelineBlocks = useMemo(() => {
    if (!reviewDetail) return [];
    return reviewTranscriptBlocksToTimeline(
      reviewDetail.transcript_blocks,
      reviewDetail.session_feedback_items,
    );
  }, [reviewDetail]);
  const [mediaState, mediaControls] = useMediaPlayer(audioTimelineBlocks);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [followAudioPlayback, setFollowAudioPlayback] = useState(false);
  const loadedMediaSessionIdRef = useRef<string | null>(null);
  const mediaRequestSessionIdRef = useRef<string | null>(null);
  const loadMediaUrl = mediaControls.loadUrl;
  const resetMedia = mediaControls.reset;
  useEffect(() => {
    setMediaError(null);
    loadedMediaSessionIdRef.current = null;
    mediaRequestSessionIdRef.current = null;
    resetMedia();
  }, [sessionId, resetMedia]);

  useEffect(() => {
    if (
      !sessionId ||
      mediaRequestSessionIdRef.current === sessionId ||
      loadedMediaSessionIdRef.current === sessionId ||
      audioTimelineBlocks.length === 0
    ) return;
    let cancelled = false;
    mediaRequestSessionIdRef.current = sessionId;
    api
      .get<SessionMediaUrlResponse>(`/sessions/${sessionId}/media-url`)
      .then((r) => {
        if (cancelled) return;
        if (r.data?.url) {
          const kind = r.data.kind === 'video' ? 'video' : 'audio';
          loadMediaUrl(r.data.url, r.data.filename ?? (kind === 'video' ? '会議動画' : '会議音声'), kind);
          loadedMediaSessionIdRef.current = sessionId;
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) {
          setMediaError('再生可能なメディアがまだアップロードされていません');
        } else {
          setMediaError('メディアの取得に失敗しました');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, audioTimelineBlocks.length, loadMediaUrl]);

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/sessions/${sessionId}`)
      .then((sessResp) => {
        setSession(sessResp.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [sessionId]);

  if (loading) return <div className="p-8 text-center text-gray-400">読み込み中...</div>;
  if (!session) return <div className="p-8 text-center text-red-500">セッションが見つかりません</div>;

  // purge 済みセッションは定型メッセージのみ表示する（フィードバック・全体分析・
  // 音声などのコンテンツは削除済みで取得できない）。
  if (session.purged) {
    return (
      <div className="p-8">
        <Link to="/sessions" className="text-sm text-blue-600 hover:underline">
          ← セッション一覧
        </Link>
        <div className="mt-8 rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <p className="text-sm text-gray-600">{SESSION_PURGED_MESSAGE}</p>
        </div>
      </div>
    );
  }

  const isCoachAckPending = session.status === 'coach_ack_pending';
  const isCompleted = session.status === 'completed';
  const isAllSkipped = session.outcome === 'all_skipped';
  const sessionComments = reviewDetail?.session_comments ?? [];
  const isCoach = user && String(session.coach_user_id) === String(user.id);
  const isAdminPreview = Boolean(user?.is_admin && !isCoach);
  const canAck = Boolean(isCoach && isCoachAckPending);
  const reviewers = Array.isArray(session.reviewers) ? session.reviewers : [];
  const hasActiveReviewAssignment = reviewers.some((reviewer) => {
    if (!reviewer || typeof reviewer !== 'object') return false;
    const row = reviewer as { reviewer_user_id?: unknown; state?: unknown };
    return (
      String(row.reviewer_user_id) === String(user?.id) &&
      (row.state === 'pending' || row.state === 'in_progress')
    );
  });
  const isFeedbackReviewPending =
    session.status === 'feedback_pending' || session.status === 'feedback_in_progress';

  if (user?.is_feedbacker && isFeedbackReviewPending && hasActiveReviewAssignment) {
    return <Navigate to={`/sessions/${sessionId}/review`} replace />;
  }

  const handleCoachAck = async () => {
    if (!sessionId) return;
    setAcking(true);
    setAckError('');
    try {
      await api.post(`/sessions/${sessionId}/coach_ack`);
      const sessResp = await api.get(`/sessions/${sessionId}`);
      setSession(sessResp.data);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { detail?: { message?: string } | string } } }).response?.data
          ?.detail;
      setAckError(typeof msg === 'string' ? msg : msg?.message ?? '確認に失敗しました');
    } finally {
      setAcking(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4">
        <Link to="/sessions" className="text-sm text-blue-600 hover:underline">
          ← セッション一覧
        </Link>
      </div>
      <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start">
        <div className="min-w-0 shrink-0 lg:max-w-sm">
          <h1 className="text-xl font-semibold text-gray-900">フィードバック</h1>
          <p className="mt-1 text-sm text-gray-500">
            {session.client_name as string} / {session.coach_name as string} —{' '}
            {formatSessionDay(session.day_type as string, session.day1_variant as string | null | undefined)} #{session.serial_no as number}
          </p>
        </div>
        <SessionMeetingSummary
          summary={reviewDetail?.session.meeting_summary}
          className="w-full lg:flex-1"
        />
        <div className="shrink-0">
          {canAck && (
            <button
              type="button"
              onClick={handleCoachAck}
              disabled={acking}
              className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {acking ? '確認中...' : '確認完了'}
            </button>
          )}
          {isCompleted && (
            <span className="inline-flex rounded bg-green-100 px-3 py-1.5 text-sm text-green-700">
              確認済み
            </span>
          )}
        </div>
      </div>
      {ackError && (
        <div className="mt-3 rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">
          エラー: {ackError}
        </div>
      )}

      {isAdminPreview && (
        <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700">
          管理者プレビューです。コメントや確認完了などのアクションはできません。
        </div>
      )}

      {isAllSkipped && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-600">今回は人手レビューなし（レビュー対象外）</p>
        </div>
      )}

      <div className="mt-4 shrink-0 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="セッション表示切替">
          <button
            type="button"
            onClick={() => setActiveTab('feedback')}
            className={`border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'feedback'
                ? 'border-blue-600 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            aria-current={activeTab === 'feedback' ? 'page' : undefined}
          >
            フィードバック
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('analysis')}
            className={`border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'analysis'
                ? 'border-blue-600 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            aria-current={activeTab === 'analysis' ? 'page' : undefined}
          >
            全体分析
          </button>
        </nav>
      </div>

      {activeTab === 'analysis' && sessionId && (
        // 久保田さん要望: 分析タブは画面表示分いっぱいに広げ、その中で内側スクロール
        // にしたい。max-h の頭打ちを撤廃して viewport - 22rem まで使う。
        // 内側 (OverallAnalysisPanel L290 相当) は既に min-h-0 flex-1 overflow-auto で
        // 親のフルサイズを取ってスクロールする。
        <div className="mt-4 flex h-[calc(100vh-22rem)] min-h-[30rem] min-w-0 flex-col">
          <OverallAnalysisPanel
            sessionId={sessionId}
            isAdmin={Boolean(user?.is_admin)}
            sessionStatus={session.status as string | null | undefined}
            markReadOnLoad={Boolean(
              user?.is_feedbacker &&
              !user?.is_admin &&
              String(session.coach_user_id) !== String(user?.id)
            )}
          />
        </div>
      )}

      {activeTab === 'feedback' && (
        <>
      {sessionComments.length > 0 && (
        <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-gray-900">セッション全体コメント</h2>
          <div className="mt-3 space-y-3">
            {sessionComments.map((comment) => (
              <article key={comment.id} className="border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span className="font-medium text-gray-700">{comment.reviewer_name}</span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-800">
                  {comment.body}
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* メディア再生バー（アップロード済み S3 メディアを自動ロード） */}
      {reviewDetail && audioTimelineBlocks.length > 0 && (
        <MediaPlaybackPanel
          state={mediaState}
          controls={mediaControls}
          loadError={mediaError}
        />
      )}

      {/* 指摘 ⇔ 文字起こしハイライト連動ビュー */}
      <div className="mt-6 flex h-[36rem] flex-col">
        {reviewDetailLoading ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-12">
            <LoadingState message="フィードバック詳細を読み込み中..." />
          </div>
        ) : reviewDetailError ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 p-8 text-sm text-amber-800">
            {reviewDetailError}
          </div>
        ) : reviewDetail ? (
          <ReviewTranscriptPanel
            detail={reviewDetail}
            showReviewerJudgments
            showAdditionalFeedback
            onUtteranceClick={
              mediaState.isLoaded ? mediaControls.seekToUtterance : undefined
            }
            activeAudioUtteranceId={mediaState.activeUtteranceId}
            isAudioPlaying={mediaState.isPlaying}
            followAudioPlayback={followAudioPlayback}
            onFollowAudioPlaybackChange={setFollowAudioPlayback}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-12 text-sm text-gray-400">
            フィードバック詳細が取得できませんでした
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}
